import React, { ReactNode } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from "recharts";

// Simple Card components (no Shadcn UI needed)
type BasicProps = {
  children: ReactNode;
  className?: string;
};

const Card = ({ children, className = "" }: BasicProps) => (
  <div className={`bg-white rounded-2xl shadow p-4 ${className}`}>{children}</div>
);

const CardHeader = ({ children, className = "" }: BasicProps) => (
  <div className={`mb-2 ${className}`}>{children}</div>
);

const CardTitle = ({ children, className = "" }: BasicProps) => (
  <h2 className={`text-lg font-semibold mb-2 ${className}`}>{children}</h2>
);

const CardContent = ({ children, className = "" }: BasicProps) => (
  <div className={className}>{children}</div>
);

// Dashboard
export default function SupportDashboard() {
  const callVolume = [
    { day: "Mon", calls: 120 },
    { day: "Tue", calls: 140 },
    { day: "Wed", calls: 100 },
    { day: "Thu", calls: 160 },
    { day: "Fri", calls: 180 },
    { day: "Sat", calls: 90 },
    { day: "Sun", calls: 60 }
  ];

  const responseTime = [
    { day: "Mon", seconds: 40 },
    { day: "Tue", seconds: 35 },
    { day: "Wed", seconds: 45 },
    { day: "Thu", seconds: 38 },
    { day: "Fri", seconds: 32 },
    { day: "Sat", seconds: 50 },
    { day: "Sun", seconds: 55 }
  ];

  const callsByAgent = [
    { agent: "Alice", calls: 200 },
    { agent: "Bob", calls: 170 },
    { agent: "Carlos", calls: 220 },
    { agent: "Diana", calls: 150 }
  ];

  return (
    <div className="grid gap-4 p-4 md:grid-cols-2 lg:grid-cols-3 bg-gray-50 min-h-screen">
      {/* KPI Cards */}
      <Card className="col-span-full grid grid-cols-2 md:grid-cols-4 gap-2">
        {[
          { label: "Calls Today", value: "162" },
          { label: "Avg Response", value: "38s" },
          { label: "Avg Duration", value: "4m 20s" },
          { label: "SLA Compliance", value: "92%" }
        ].map((kpi) => (
          <CardContent key={kpi.label} className="text-center">
            <p className="text-sm text-gray-500">{kpi.label}</p>
            <p className="text-2xl font-bold text-gray-800">{kpi.value}</p>
          </CardContent>
        ))}
      </Card>

      {/* Call Volume Trend */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>Call Volume Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={callVolume}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="calls" stroke="#3b82f6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Avg Response Time */}
      <Card>
        <CardHeader>
          <CardTitle>Average Response Time</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={responseTime}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="seconds" stroke="#10b981" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Calls by Agent */}
      <Card className="lg:col-span-1">
        <CardHeader>
          <CardTitle>Calls by Agent</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={callsByAgent}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="agent" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="calls" fill="#6366f1" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
